import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { Infrastructure } from '../../models/infrastructure/Infrastructure.model';
import { User } from '../../models/infrastructure/User.model';
import { MessageService } from '../message/message.service';
import { Organization, IUrls } from '../../models/infrastructure/Organization.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GLOBAL_CONFIG } from '../../data/globalConfig';
import { DocumentationUrlsModel } from '../../models/infrastructure/DocumentationUrlsModel.model';
import { Version } from '../../models/infrastructure/Version.model';
import { ResponseMessage } from '../../models/infrastructure/ResponseMessage.model';
import { AppService } from '../app/app.service';
import { OrganizationStatus } from '../../models/infrastructure/OrganizationStatus.model';
import { Secret } from '../../models/infrastructure/Secret.model';
import { AcrJobTokenStatus } from '../../models/infrastructure/AcrJobTokenStatus.model';

@Injectable({
  providedIn: 'root',
})
export class InfrastructureService {
  private backendInfra = GLOBAL_CONFIG.config.backendHost + '/infra';
  public organizations: Organization[] = [];
  jsonContentHeader = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  private infra = new BehaviorSubject(null);
  currentInfra = this.infra.asObservable();
  private interval: any;

  private locked = new BehaviorSubject(false);
  currentLocked = this.locked.asObservable();

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private snackBar: MatSnackBar,
    private appService: AppService
  ) { }

  refreshGlobalStatus() {
    const loading = this.appService.openLoading();
    this.getGlobalInfraStatus().subscribe(infrastructure => {
      this.infra.next(infrastructure)
      loading.close();
    });
  }

  getGlobalInfraStatus(): Observable<Infrastructure> {
    return this.http.get<any>(this.backendInfra + '/').pipe(
      map((v) => v.infrastructure),
      tap((_) => this.log(`assignment found`)),
      catchError(this.handleError<Infrastructure>('getInfrastructure'))
    );
  }

  getAdminsUsersOrg(org: Organization): Observable<Organization> {
    let params = new HttpParams().set('shortName', org.shortName);
    let headers= new HttpHeaders().set('Content-Type', 'application/json');

    return this.http.get<any>(this.backendInfra + '/org/users', {headers: headers, params: params})
      .pipe(
        map((v) => {
          var org: Organization = v.organization;
          org = this.updateOrgGrafanaLink(org);
          return org;
        }),
        tap((_) => this.log(`get /org/users organization failed`)),
        catchError(this.handleError<Organization>('get /org/users organization'))
      );
  }

  getUserLogged(): Observable<User> {
    return this.http.get<any>(this.backendInfra + '/user-logged/').pipe(
      map((v) => v.user),
      tap((_) => this.log(`logged user failed`)),
      catchError(this.handleError<User>('get user logged'))
    );
  }

  addOrganizationInfra(org: Organization): Observable<Organization> {
    return this.http
      .post<any>(
        this.backendInfra + '/org',
        JSON.stringify(org),
        this.jsonContentHeader
      )
      .pipe(
        map((v) => v.organization),
        tap((_) => this.log(`add Organization failed`)),
        catchError(this.handleError<Organization>('add Organization'))
      );
  }

  deleteOrganizationInfra(org: Organization): Observable<Organization> {
    return this.http
      .delete<any>(
        this.backendInfra + '/org/' + org.shortName,
        this.jsonContentHeader
      )
      .pipe(
        tap((_) => this.log(`delete Organization failed`)),
        catchError(this.handleError<Organization>('delete Organization'))
      );
  }

  updateOrganizationInfra(org: Organization): Observable<Organization> {
    return this.http
      .put<any>(
        this.backendInfra + '/org/' + org.shortName,
        JSON.stringify(org),
        this.jsonContentHeader
      )
      .pipe(
        map((v) => v.organization),
        tap((_) => this.log(`update Organization failed`)),
        catchError(this.handleError<Organization>('update Organization'))
      );
  }

  updateGlobalAdminsInfra(infra: Infrastructure): Observable<Infrastructure> {
    return this.http
      .put<any>(
        this.backendInfra + '/global-admins-infra/',
        JSON.stringify(infra),
        this.jsonContentHeader
      )
      .pipe(
        map((v) => v.infrastructure),
        tap((_) => this.log(`update Infrastructure Global Admins failed`)),
        catchError(this.handleError<Infrastructure>('update Global Admins'))
      );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      // console.error(error); // log to console instead
      let errorMsg: string = error.error;
      if (typeof errorMsg !== 'string') {
        errorMsg = error.message;
      }
      if (errorMsg.includes('Token renewal operation failed')) {
        localStorage.clear();
        location.reload();
      }
      const re = /,/gi;
      this.snackBar.open(errorMsg.replace(re, '\n\r'), null, {
        duration: 5000,
        panelClass: 'failed-snackbar'
      });

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result);
    };
  }

  /** Log a BitbucketService message with the InfrastructureService */
  private log(message: string) {
    this.messageService.add(`InfrastructureService: ${message}`);
  }

  /**
   * Get Documentation URLs
   */
  getDocumentationUrls(): Observable<DocumentationUrlsModel> {
    return this.http.get<any>(this.backendInfra + '/documentation-urls/').pipe(
      map((v) => v),
      tap((_) => this.log(`get Documentation failed`)),
      catchError(this.handleError<DocumentationUrlsModel>('get Documentation success'))
    );
  }

  /**
   * Get API Version
   */
  getAPIVersion(): Observable<Version> {
    return this.http.get<any>(this.backendInfra + '/version').pipe(
      map((v) => v),
      tap((_) => this.log(`get Version failed`)),
      catchError(this.handleError<Version>('get Version success'))
    );
  }

  updateOrganizationJenkinsConfigVersion(org: Organization): Observable<ResponseMessage> {

    let params = new HttpParams().set('shortName', org.shortName).set('jenkinsConfigVersion', org.jenkinsConfig);
    let headers= new HttpHeaders().set('Content-Type', 'application/json');

    return this.http
      .put<any>(this.backendInfra + '/update-jenkins-config', {}, {headers: headers, params: params})
      .pipe(
        map((v) => v),
        tap((_) => this.log(`update Organization failed`)),
        catchError(this.handleError<ResponseMessage>('update jenkins config'))
      );
  }

  getEmailUsername(username: String): Observable<ResponseMessage> {
    return this.http.get<any>(this.backendInfra + '/email-user/' + username).pipe(
      map((v) => v),
      tap((_) => this.log(`email found`)),
      catchError(this.handleError<ResponseMessage>('getEmailUsername'))
    );
  }

  checkOrganizationStatus(organizationName: string, organizationShortName: string): Observable<OrganizationStatus> {
    const url = this.backendInfra + '/kubernetes/organization/status';
    let params = new HttpParams().set('organizationName', organizationName).set('organizationShortName', organizationShortName);
    let headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<any>(url, {headers: headers, params: params}).pipe(
      map((v) => {
        const a: OrganizationStatus = v;
        return a;
      }),
      tap((_) => this.log('Check organization ' + organizationName + 'status is failed')),
      catchError(this.handleError<OrganizationStatus>('Error checking organization ' + organizationName + ' status'))
    )
  }

  scaleUpOrganization(organizationName: string, organizationShortName: string, version: string): Observable<ResponseMessage> {
    const url = this.backendInfra + '/kubernetes/organization/scaleup';
    let params = new HttpParams().set('organizationName', organizationName).set('organizationShortName', organizationShortName).set('version', version);
    let headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<any>(url, {headers: headers, params: params}).pipe(
      map((v) => {
        const a: ResponseMessage = v;
        return a;
      }),
      tap((_) => this.log('Scale up organization ' + organizationName + 'is failed')),
      catchError(this.handleError<ResponseMessage>('Error scaling up organization ' + organizationName))
    )
  }

  scaleDownOrganization(organizationName: string, organizationShortName: string, version: string): Observable<ResponseMessage> {
    const url = this.backendInfra + '/kubernetes/organization/scaledown';
    let params = new HttpParams().set('organizationName', organizationName).set('organizationShortName', organizationShortName).set('version', version);
    let headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<any>(url, {headers: headers, params: params}).pipe(
      map((v) => {
        const a: ResponseMessage = v;
        return a;
      }),
      tap((_) => this.log('Scale down organization ' + organizationName + 'is failed')),
      catchError(this.handleError<ResponseMessage>('Error scaling down organization ' + organizationName))
    )
  }

  getAcrOrganizationTokens(organizationShortName: string): Observable<Array<Secret>> {
    const url = this.backendInfra + '/acr/organization-tokens';
    let params = new HttpParams().set('orgShortName', organizationShortName)
    let headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<any>(url, {headers: headers, params: params}).pipe(
      map((v) => {
        const a: Array<Secret> = v;
        return a
      })
    )
  }

  generateAcrToken(organizationShortName: string): Observable<ResponseMessage> {
    const url = this.backendInfra + '/acr/generate-token';
    let params = new HttpParams().set('orgShortName', organizationShortName)
    let headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<any>(url, {headers: headers, params: params}).pipe(
      map((v) => {
        const a: ResponseMessage = v;
        return a;
      })
    )
  }

  checkAcrTokenCreation(organizationShortName: string): Observable<AcrJobTokenStatus> {
    const url = this.backendInfra + '/acr/token-creation-status';
    let params = new HttpParams().set('orgShortName', organizationShortName)
    let headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<any>(url, {headers: headers, params: params}).pipe(
      map((v) => {
        const a: AcrJobTokenStatus = v;
        return a;
      })
    )
  }

  updateOrgGrafanaLink(org: Organization): Organization{
    const urlJenkinsValue: string = org.urls.find(url => url.id === "Jenkins").value;
    const urlGrafanaValue: string = urlJenkinsValue.replace("/jenkins","/grafana");
    const urlGrafana: IUrls = {id: "Grafana", value: urlGrafanaValue};
    org.urls.push(urlGrafana);
    return org;
  }
}
